import { useState, useEffect } from 'react'
import { Bell } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { ROUTES } from '@/constants'
import { notificationService } from '@/services/firebase/notifications'

const ROUTE_META: Record<string, { title: string; sub?: string }> = {
  [ROUTES.TASKS]:         { title: 'Tasks',         sub: 'Your field assignments' },
  [ROUTES.NEW_TASK]:      { title: 'New Task',       sub: 'Create a task' },
  [ROUTES.INQUIRIES]:     { title: 'Enquiries',      sub: 'Customer interest log' },
  [ROUTES.NEW_INQUIRY]:   { title: 'New Enquiry',    sub: 'Register interest' },
  [ROUTES.INSTALLATIONS]: { title: 'Installations',  sub: 'Active & pending' },
  [ROUTES.TICKETS]:       { title: 'Tickets',        sub: 'Support tickets' },
  [ROUTES.NEW_TICKET]:    { title: 'New Ticket',     sub: 'Raise a support ticket' },
  [ROUTES.FOLLOWUPS]:     { title: 'Follow-ups',     sub: 'Scheduled follow-ups' },
  [ROUTES.ENGINEERS]:     { title: 'Users',          sub: 'Engineers & admins' },
  [ROUTES.CUSTOMERS]:        { title: 'Customers',        sub: 'BSNL customer data' },
  [ROUTES.NETWORK_TOPOLOGY]: { title: 'Network Topology', sub: 'GPON fiber hierarchy' },
  [ROUTES.REPORTS]:       { title: 'Reports',        sub: 'Analytics & performance' },
  [ROUTES.SETTINGS]:      { title: 'Settings',       sub: 'App preferences' },
  [ROUTES.PROFILE]:       { title: 'Profile',        sub: 'Your account' },
  [ROUTES.NOTIFICATIONS]: { title: 'Notifications',  sub: 'Recent alerts' },
}

const getMeta = (path: string) => {
  if (ROUTE_META[path]) return ROUTE_META[path]
  for (const [route, meta] of Object.entries(ROUTE_META)) {
    const pattern = route.replace(/:[^/]+/g, '[^/]+')
    if (new RegExp(`^${pattern}$`).test(path)) return meta
  }
  return null
}

const Header = () => {
  const location = useLocation()
  const { user } = useAuthStore()
  const [unreadCount, setUnreadCount] = useState(0)

  useEffect(() => {
    if (!user?.uid) return
    const unsub = notificationService.subscribeToNotifications(user.uid, items => {
      setUnreadCount(items.filter(n => !n.read).length)
    })
    return () => unsub()
  }, [user?.uid])

  const isDashboard = location.pathname === ROUTES.DASHBOARD
  const meta = getMeta(location.pathname)

  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening'
  const firstName = user?.name?.split(' ')[0] ?? 'there'
  const initials = user?.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() ?? 'U'

  return (
    <header className="sticky top-0 z-40 shrink-0 overflow-hidden"
      style={{ background: 'linear-gradient(135deg,#050d1f 0%,#0c1e3d 60%,#0d2a50 100%)' }}>

      {/* Ambient glow orb */}
      <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle,rgba(59,130,246,0.25) 0%,transparent 65%)', filter: 'blur(32px)' }} />

      <div className="relative z-10 flex items-center justify-between px-4 py-3.5 gap-3">

        {/* Left: branding or page title */}
        {isDashboard ? (
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-blue-300/60 uppercase tracking-widest leading-none mb-0.5">
              Good {greeting}
            </p>
            <h1 className="text-[17px] font-black text-white leading-tight truncate">
              {firstName} 👋
            </h1>
          </div>
        ) : (
          <div className="flex-1 min-w-0">
            <h1 className="text-[17px] font-black text-white leading-tight truncate">
              {meta?.title ?? 'BSNL FTTH'}
            </h1>
            {meta?.sub && (
              <p className="text-[11px] text-blue-300/60 mt-0.5 truncate">{meta.sub}</p>
            )}
          </div>
        )}

        {/* Right: bell + avatar */}
        <div className="flex items-center gap-2 shrink-0">
          <Link to={ROUTES.NOTIFICATIONS}
            className="relative w-9 h-9 flex items-center justify-center rounded-2xl transition-all active:scale-90"
            style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <Bell size={16} className="text-white" />
            {unreadCount > 0 && (
              <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-orange-400" />
            )}
          </Link>

          <Link to={ROUTES.PROFILE}
            className="flex items-center gap-2 pl-1 pr-2.5 py-1 rounded-2xl transition-all active:scale-95"
            style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="w-7 h-7 rounded-xl flex items-center justify-center text-white text-[11px] font-black shrink-0"
              style={{ background: 'linear-gradient(135deg,#6366f1,#3b82f6)' }}>
              {initials}
            </div>
            <div className="min-w-0">
              <p className="text-[12px] font-bold text-white truncate max-w-[72px] leading-tight">{firstName}</p>
              <p className="text-[9px] text-blue-300/60 leading-tight">
                {user?.role === 'admin' ? 'Admin' : 'Engineer'}
              </p>
            </div>
          </Link>
        </div>
      </div>
    </header>
  )
}

export default Header
